import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  Button,
  TextField,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import AppraisalViewController from "../../redux/actions/AppraisalAction";
import FormDrawer from "../../components/layout/FormDrawer";
import { primaryButtonSx, inputSx } from "../../components/auth/authTheme";

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "in_review", label: "In Review" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
  { value: "applied", label: "Applied to Payroll" },
];

function AppraisalCycleFormModal({ show, handleClose, initialData = null }) {
  const dispatch = useDispatch();
  const { addAppraisalCycle, updateAppraisalCycle, getEmployeeForAppraisalCycle } =
    AppraisalViewController();
  const { all_employee, isLoding } = useSelector((state) => state.apprisalcycle);

  const isEdit = Boolean(initialData?.id);

  const [initialValues, setInitialValues] = useState({
    employee: "",
    cycle_start: "",
    cycle_end: "",
    old_salary: "",
    hike_percentage: "",
    effective_from: "",
    average_rating: "",
    status: "draft",
  });

  useEffect(() => {
    if (!show) return;
    dispatch(getEmployeeForAppraisalCycle());
  }, [show, dispatch]);

  useEffect(() => {
    if (!show) return;

    if (initialData) {
      setInitialValues({
        employee: initialData.employee || "",
        cycle_start: initialData.cycle_start || "",
        cycle_end: initialData.cycle_end || "",
        old_salary: initialData.old_salary || "",
        hike_percentage: initialData.hike_percentage || "",
        effective_from: initialData.effective_from || "",
        average_rating: initialData.average_rating || "",
        status: initialData.status || "draft",
      });
    } else {
      setInitialValues({
        employee: "",
        cycle_start: "",
        cycle_end: "",
        old_salary: "",
        hike_percentage: "",
        effective_from: "",
        average_rating: "",
        status: "draft",
      });
    }
  }, [show, initialData?.id]);

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      const action = isEdit
        ? updateAppraisalCycle(initialData.id, values)
        : addAppraisalCycle(values);
      await dispatch(action);
      resetForm();
      handleClose();
    } catch (error) {
      console.error("Error submitting appraisal form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        values,
        setFieldValue,
        handleChange,
        isSubmitting,
        submitForm,
      }) => {
        const isSaving = isSubmitting || isLoding;

        return (
          <Form style={{ display: "contents" }}>
            <FormDrawer
              open={show}
              onClose={handleClose}
              width={{ xs: "100%", sm: 560, md: 760 }}
              title={isEdit ? "Edit Appraisal Cycle" : "Add Appraisal Cycle"}
              subtitle={
                isEdit
                  ? "Update cycle dates, hike, and status"
                  : "Set up a new appraisal cycle for an employee"
              }
              footer={
                <>
                  <Button onClick={handleClose} color="inherit" disabled={isSaving}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    disabled={isSaving}
                    onClick={() => submitForm()}
                    sx={{
                      ...primaryButtonSx,
                      minWidth: 120,
                    }}
                    startIcon={
                      isSaving ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                  >
                    {isSaving
                      ? isEdit
                        ? "Updating..."
                        : "Saving..."
                      : isEdit
                        ? "Update"
                        : "Submit"}
                  </Button>
                </>
              }
            >
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={all_employee || []}
                    getOptionLabel={(option) =>
                      option.full_name ||
                      `${option.first_name || ""} ${option.last_name || ""}`.trim()
                    }
                    value={
                      all_employee?.find((emp) => emp.id === values.employee) ||
                      null
                    }
                    onChange={(_, newValue) => {
                      if (newValue) {
                        setFieldValue("employee", newValue.id);
                        setFieldValue("old_salary", newValue.basic_salary || "");
                      } else {
                        setFieldValue("employee", "");
                        setFieldValue("old_salary", "");
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Employee *" sx={inputSx} />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Cycle Start *"
                    name="cycle_start"
                    value={values.cycle_start}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Cycle End *"
                    name="cycle_end"
                    value={values.cycle_end}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Old Salary"
                    name="old_salary"
                    value={values.old_salary}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Hike Percentage (%)"
                    name="hike_percentage"
                    value={values.hike_percentage}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Effective From"
                    name="effective_from"
                    value={values.effective_from}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    inputProps={{ step: "0.01" }}
                    label="Average Rating"
                    name="average_rating"
                    value={values.average_rating}
                    onChange={handleChange}
                    sx={inputSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    name="status"
                    value={values.status}
                    onChange={handleChange}
                    sx={inputSx}
                  >
                    {statusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </FormDrawer>
          </Form>
        );
      }}
    </Formik>
  );
}

export default AppraisalCycleFormModal;
